<template>
  <v-dialog
    v-model="internalDialog"
    max-width="500"
    persistent
  >
    <v-card>
      <v-card-title>
        {{ formTitle }}
      </v-card-title>

      <v-card-text>
        <v-form
          ref="form"
          v-model="formValid"
        >
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="selectedVendible"
                :items="items"
                :loading="isLoadingItems"
                item-text="name"
                item-value="id"
                label="アイテムから選択（任意）"
                clearable
                return-object
                hint="アイテムを選択するとボトル名が自動入力されます"
                persistent-hint
                @change="onItemSelect"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.name"
                label="ボトル名"
                :rules="[v => !!v || 'ボトル名は必須です']"
                required
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.tagName"
                label="タグ名（ボトルに貼るラベルの名前）"
                hint="お客様の本名やニックネームなど"
                persistent-hint
              />
            </v-col>

            <v-col cols="12">
              <v-menu
                transition="scale-transition"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="formData.keptAt"
                    label="キープ開始日"
                    readonly
                    :rules="[v => !!v || 'キープ開始日は必須です']"
                    required
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="formData.keptAt"
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  no-title
                />
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.locationMemo"
                label="配置場所メモ"
                rows="2"
                auto-grow
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="onCancel"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSaving"
          :disabled="!formValid || isSaving"
          @click="onSave"
        >
          {{ isEditing ? '更新' : '追加' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
} from '@vue/composition-api'
import { format } from 'date-fns'
import VendibleApi from '@/api/waiter/Vendible'

export default {
  name: 'CustomerBottleForm',
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    editingBottle: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const form = ref(null)
    const formValid = ref(false)
    const isSaving = ref(false)
    const items = ref([])
    const isLoadingItems = ref(false)
    const selectedVendible = ref(null)

    const formData = ref({
      name: '',
      tagName: '',
      keptAt: format(new Date(), 'yyyy-MM-dd'),
      locationMemo: '',
      vendibleId: null,
      vendibleType: null,
    })

    const internalDialog = computed({
      get: () => props.dialog,
      set: val => {
        if (!val) emit('close')
      },
    })

    const isEditing = computed(() => {
      return props.editingBottle && props.editingBottle.id
    })

    const formTitle = computed(() => {
      return isEditing.value ? 'ボトルを編集' : 'ボトルを追加'
    })

    // ボトルキープはアイテムのみなので、vendibleTypeは常に'Item'
    const getVendibleType = jsonapiType => {
      if (!jsonapiType) return null
      const type = jsonapiType.toLowerCase()

      return (type === 'items' || type === 'item') ? 'Item' : null
    }

    const fetchItems = async () => {
      isLoadingItems.value = true
      try {
        const res = await VendibleApi.getVendibles()
        if (res?.status === 200 && res.data?.vendibles?.data) {
          // アイテムのみをフィルタリング
          items.value = res.data.vendibles.data
            .filter(item => {
              const type = (item.type || '').toLowerCase()

              return type === 'items' || type === 'item'
            })
            .map(item => ({
              id: item.id,
              name: item.attributes?.name || item.name,
              type: 'Item',
            }))
        }
      } catch (error) {
        console.error('Failed to fetch items:', error)
      } finally {
        isLoadingItems.value = false
      }
    }

    const onItemSelect = item => {
      if (item) {
        formData.value.name = item.name
        formData.value.vendibleId = item.id
        formData.value.vendibleType = item.type
      } else {
        formData.value.vendibleId = null
        formData.value.vendibleType = null
      }
    }

    const initializeForm = () => {
      if (props.editingBottle) {
        const vendibleType = props.editingBottle.vendible?.type
          ? getVendibleType(props.editingBottle.vendible.type)
          : null
        formData.value = {
          name: props.editingBottle.name || '',
          tagName: props.editingBottle.tagName || '',
          keptAt: props.editingBottle.keptAt || format(new Date(), 'yyyy-MM-dd'),
          locationMemo: props.editingBottle.locationMemo || '',
          vendibleId: props.editingBottle.vendible?.id || null,
          vendibleType,
        }
        // 編集時は既存のvendibleを選択状態にする
        if (props.editingBottle.vendible) {
          selectedVendible.value = {
            id: props.editingBottle.vendible.id,
            name: props.editingBottle.vendible.name,
            type: vendibleType,
          }
        } else {
          selectedVendible.value = null
        }
      } else {
        formData.value = {
          name: '',
          tagName: '',
          keptAt: format(new Date(), 'yyyy-MM-dd'),
          locationMemo: '',
          vendibleId: null,
          vendibleType: null,
        }
        selectedVendible.value = null
      }
      isSaving.value = false
    }

    const onSave = async () => {
      if (!form.value || !await form.value.validate()) return
      if (isSaving.value) return

      isSaving.value = true

      emit('save', {
        name: formData.value.name,
        tagName: formData.value.tagName || null,
        keptAt: formData.value.keptAt,
        locationMemo: formData.value.locationMemo || null,
        vendibleId: formData.value.vendibleId,
        vendibleType: formData.value.vendibleType,
      })

      isSaving.value = false
    }

    const onCancel = () => {
      emit('close')
    }

    // dialogが開いたらフォームを初期化
    watch(() => props.dialog, newVal => {
      if (newVal) {
        initializeForm()
      }
    }, { immediate: true })

    // editingBottleが変更されたらフォームを初期化
    watch(() => props.editingBottle, () => {
      if (props.dialog) {
        initializeForm()
      }
    })

    // 初期化時にアイテムリストを取得
    onMounted(() => {
      fetchItems()
    })

    return {
      form,
      formValid,
      isSaving,
      formData,
      items,
      isLoadingItems,
      selectedVendible,
      internalDialog,
      isEditing,
      formTitle,
      onItemSelect,
      onSave,
      onCancel,
    }
  },
}
</script>
